import * as React from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COUNTRY_CODES, parsePhoneWithCode, formatPhoneWithCode, DEFAULT_COUNTRY_CODE } from "@/lib/country-codes";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

interface PhoneInputWithCodeProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const MAX_DIGITS_INDIA = 10;

export function PhoneInputWithCode({
  value,
  onChange,
  placeholder = "Phone number",
  disabled = false,
  className,
}: PhoneInputWithCodeProps) {
  // Parse the incoming value to get country code and number
  const parsed = React.useMemo(() => parsePhoneWithCode(value), [value]);
  
  const [selectedDialCode, setSelectedDialCode] = React.useState(parsed.countryCode.dialCode);
  const [phoneNumber, setPhoneNumber] = React.useState(parsed.number);

  // Update internal state when value prop changes
  // Also auto-format raw numbers without country code to ensure consistency
  React.useEffect(() => {
    const newParsed = parsePhoneWithCode(value);
    setSelectedDialCode(newParsed.countryCode.dialCode);
    setPhoneNumber(newParsed.number);
    
    // If incoming value is a raw number (no country code), call onChange with formatted value
    if (value && !value.startsWith('+') && newParsed.number) {
      const formatted = formatPhoneWithCode(newParsed.countryCode.dialCode, newParsed.number);
      if (formatted !== value) {
        onChange?.(formatted);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleDialCodeChange = (dialCode: string) => {
    setSelectedDialCode(dialCode);
    const formatted = formatPhoneWithCode(dialCode, phoneNumber);
    onChange?.(formatted);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newNumber = e.target.value.replace(/[^\d]/g, ""); // Only allow digits
    
    // Limit to max 10 digits for India
    const maxDigits = selectedDialCode === "+91" ? MAX_DIGITS_INDIA : 15;
    if (newNumber.length > maxDigits) {
      newNumber = newNumber.slice(0, maxDigits);
    }
    
    setPhoneNumber(newNumber);
    const formatted = formatPhoneWithCode(selectedDialCode, newNumber);
    onChange?.(formatted);
  };

  // Find the selected country for display
  const selectedCountry = COUNTRY_CODES.find(c => c.dialCode === selectedDialCode) || DEFAULT_COUNTRY_CODE;
  
  // Validation state
  const isIndian = selectedDialCode === "+91";
  const digitCount = phoneNumber.length;
  const isValid = isIndian ? digitCount === MAX_DIGITS_INDIA : digitCount >= 7 && digitCount <= 15;
  return (
    <div className={cn("flex gap-2", className)}>
      <Select
        value={selectedDialCode}
        onValueChange={handleDialCodeChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-[100px] flex-shrink-0">
          <SelectValue>
            <span className="flex items-center gap-1">
              <span>{selectedCountry.flag}</span>
              <span className="text-xs">{selectedCountry.dialCode}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {COUNTRY_CODES.map((country) => (
            <SelectItem key={country.code} value={country.dialCode}>
              <span className="flex items-center gap-2">
                <span>{country.flag}</span>
                <span className="text-sm">{country.name}</span>
                <span className="text-xs text-muted-foreground">{country.dialCode}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="relative flex-1">
        <Input
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={isIndian ? MAX_DIGITS_INDIA : 15}
          className={cn(
            "pr-8",
            isIndian && digitCount > 0 && !isValid && "border-destructive focus-visible:ring-destructive",
            isIndian && isValid && "border-green-500 focus-visible:ring-green-500"
          )}
        />
        {isIndian && isValid && (
          <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
        )}
      </div>
    </div>
  );
}
